// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authApi } from '../services/apiClient';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';
import * as pwaDetection from '../utils/pwaDetection';
import { initializeSessionMonitoring } from '../utils/sessionRecovery';
import CacheInitializer from '../services/cacheInitializer';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPWA, setIsPWA] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [realUser, setRealUser] = useState(null);

  useEffect(() => {
    // Initialize PWA detection and device info
    const initializeAuth = async () => {
      try {
        // Detect if running as PWA
        const pwaInstalled = pwaDetection.isPWAInstalled();
        setIsPWA(pwaInstalled);

        // Get or generate device info
        let storedDeviceInfo = await pwaDetection.getStoredDeviceInfo();
        if (!storedDeviceInfo) {
          storedDeviceInfo = await pwaDetection.getDeviceInfo();
          await pwaDetection.storeDeviceInfo();
        }
        setDeviceInfo(storedDeviceInfo);

        // Migrate from localStorage to IndexedDB if needed
        if (indexedDB.isAvailable()) {
          const hasOldTokens = localStorage.getItem('auth_token') || localStorage.getItem('refresh_token');
          if (hasOldTokens) {
            await indexedDB.migrateFromLocalStorage(['auth_token', 'refresh_token', 'user']);

            // Clear old localStorage items
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
          }
        }

        // Restore impersonation state from sessionStorage (survives page refresh)
        try {
          const impersonationState = sessionStorage.getItem('caro_impersonation');
          if (impersonationState) {
            const parsed = JSON.parse(impersonationState);
            if (parsed.isImpersonating && parsed.realUser) {
              setIsImpersonating(true);
              setRealUser(parsed.realUser);
            }
          }
        } catch (e) {
          sessionStorage.removeItem('caro_impersonation');
        }

        // Check authentication status
        await checkAuth();
      } catch (err) {
        setLoading(false);
      }
    };

    initializeAuth();

    // Initialize automatic session monitoring
    // This checks token health periodically and handles recovery
    initializeSessionMonitoring();

    // Note: Session check interval is now handled by axios interceptor
    // which automatically refreshes tokens before they expire
  }, []);

  const checkAuth = async () => {
    try {
      // Check if we have a token (try IndexedDB first, fallback to localStorage)
      let token = null;
      let encryptedToken = null;

      if (indexedDB.isAvailable()) {
        encryptedToken = await indexedDB.getItem('auth_token');

        if (encryptedToken) {
          try {
            token = await tokenEncryption.decryptToken(encryptedToken);
          } catch (decryptError) {
            // Clear corrupted encrypted token
            await indexedDB.removeItem('auth_token');
            await indexedDB.removeItem('refresh_token');
            await indexedDB.removeItem('user');

            // Try localStorage as fallback
            token = localStorage.getItem('auth_token');
            if (!token) {
              setLoading(false);
              return false;
            }
          }
        }
      } else {
        token = localStorage.getItem('auth_token');
      }

      if (!token) {
        setLoading(false);
        return false;
      }

      // Get current user from API (this validates the token)
      const response = await authApi.getCurrentUser();

      if (response.data.success && response.data.user) {
        setUser(response.data.user);

        // Store encrypted user data
        if (indexedDB.isAvailable()) {
          const encryptedUserData = await tokenEncryption.encryptUserData(response.data.user);
          await indexedDB.setItem('user', encryptedUserData);
        } else {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        // Initialize caches in the background after successful authentication
        // This runs asynchronously and doesn't block the UI
        CacheInitializer.initializeAll().catch(err => {
          console.warn('[AuthContext] Cache initialization failed:', err);
          // Don't throw - allow app to continue even if cache fails
        });

        setLoading(false);
        return true;
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      // Determine error type for better messaging
      const is401 = err.response?.status === 401;
      const isNetworkError = err.code === 'ERR_NETWORK' || !err.response;

      if (is401) {
        setError('Your session has expired. Please log in again.');
      } else if (isNetworkError) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else {
        setError(err.message || 'Authentication failed');
      }

      setLoading(false);

      // Automatically clear invalid auth data (don't make user do it manually!)
      try {
        if (indexedDB.isAvailable()) {
          await indexedDB.removeItem('auth_token');
          await indexedDB.removeItem('refresh_token');
          await indexedDB.removeItem('user');
        }
        // Always clear localStorage as well (belt and suspenders)
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } catch (clearError) {
        // Silent fail
      }

      return false;
    }
  };

  const login = async (token, refreshToken, redirectTo = '/dashboard') => {
    try {
      setError(null);

      // If tokens are provided, this is being called from the auth callback
      if (token) {
        // Store tokens using IndexedDB with encryption
        if (indexedDB.isAvailable()) {
          const encryptedToken = await tokenEncryption.encryptToken(token);
          await indexedDB.setItem('auth_token', encryptedToken);

          if (refreshToken) {
            const encryptedRefreshToken = await tokenEncryption.encryptToken(refreshToken);
            await indexedDB.setItem('refresh_token', encryptedRefreshToken);
          }
        } else {
          // Fallback to localStorage
          localStorage.setItem('auth_token', token);

          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
          }
        }

        // Fetch the user data with the token
        const response = await authApi.getCurrentUser();

        if (response.data.success && response.data.user) {
          setUser(response.data.user);

          // Store encrypted user data
          if (indexedDB.isAvailable()) {
            const encryptedUserData = await tokenEncryption.encryptUserData(response.data.user);
            await indexedDB.setItem('user', encryptedUserData);
          } else {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }

          // Initialize caches in the background after successful login
          CacheInitializer.initializeAll().catch(err => {
            console.warn('[AuthContext] Cache initialization failed:', err);
          });

          return { success: true };
        } else {
          throw new Error('Invalid user data received');
        }
      } else {
        // This is a normal login request, redirect to Microsoft login

        // Auto-detect environment based on hostname
        const isDevelopment =
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          process.env.NODE_ENV === 'development';

        // Use environment-specific URLs
        const API_URL = isDevelopment
          ? (process.env.REACT_APP_API_URL || 'https://api.caro.care')
          : 'https://api.caro.care';

        // Dynamically determine frontend URL based on current environment
        // For production domain (portal.caro.care), always use HTTPS without port
        let frontendUrl;
        if (isDevelopment) {
          // Development: use current hostname and port
          frontendUrl = `http://${window.location.hostname}:${window.location.port || 3032}`;
        } else {
          // Production: force HTTPS and remove port for portal.caro.care
          if (window.location.hostname === 'portal.caro.care') {
            frontendUrl = 'https://portal.caro.care';
          } else {
            // Fallback for other production domains
            frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://portal.caro.care';
          }
        }

        // Get device info for backend
        const currentDeviceInfo = deviceInfo || await pwaDetection.getDeviceInfo();

        // Add PWA and device info to the auth URL
        const callbackUrl = encodeURIComponent(`${frontendUrl}/auth-callback`);
        const authUrl = `${API_URL}/api/auth/microsoft?callbackUrl=${callbackUrl}&redirectTo=${encodeURIComponent(redirectTo)}&isPWA=${isPWA}&deviceFingerprint=${encodeURIComponent(currentDeviceInfo.deviceFingerprint)}&deviceName=${encodeURIComponent(currentDeviceInfo.deviceName)}`;

        // Redirect to the Microsoft authentication endpoint
        window.location.href = authUrl;
        return { success: true };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const logout = async () => {
    try {
      // Get refresh token to send to backend for revocation
      let refreshToken = null;

      if (indexedDB.isAvailable()) {
        const encryptedRefreshToken = await indexedDB.getItem('refresh_token');
        if (encryptedRefreshToken) {
          refreshToken = await tokenEncryption.decryptToken(encryptedRefreshToken);
        }
      } else {
        refreshToken = localStorage.getItem('refresh_token');
      }

      // Call the logout API to invalidate the session and revoke refresh token
      await authApi.logout(refreshToken);
    } catch (err) {
      // Continue with local logout even if API call fails
    }

    // Clear storage (IndexedDB or localStorage)
    if (indexedDB.isAvailable()) {
      await indexedDB.removeItem('auth_token');
      await indexedDB.removeItem('refresh_token');
      await indexedDB.removeItem('user');
    } else {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }

    // Reset cache initializer (stops auto-refresh and invalidates caches)
    CacheInitializer.reset();

    // Clear user state
    setUser(null);

    return true;
  };

  const handleAuthCallback = async (token, refreshToken) => {
    if (!token) {
      return false;
    }

    try {
      // Store the tokens using the login function
      const result = await login(token, refreshToken);
      return result.success;
    } catch (err) {
      return false;
    }
  };

  // Helper function to get current access token
  const getAccessToken = async () => {
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem('auth_token');
      if (encryptedToken) {
        return await tokenEncryption.decryptToken(encryptedToken);
      }
    } else {
      return localStorage.getItem('auth_token');
    }
    return null;
  };

  // Helper function to get current refresh token
  const getRefreshToken = async () => {
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem('refresh_token');
      if (encryptedToken) {
        return await tokenEncryption.decryptToken(encryptedToken);
      }
    } else {
      return localStorage.getItem('refresh_token');
    }
    return null;
  };

  // Helper function to set access token
  const setAccessToken = async (token) => {
    if (indexedDB.isAvailable()) {
      const encryptedToken = await tokenEncryption.encryptToken(token);
      await indexedDB.setItem('auth_token', encryptedToken);
    } else {
      localStorage.setItem('auth_token', token);
    }
  };

  // Helper function to set refresh token
  const setRefreshToken = async (token) => {
    if (indexedDB.isAvailable()) {
      const encryptedToken = await tokenEncryption.encryptToken(token);
      await indexedDB.setItem('refresh_token', encryptedToken);
    } else {
      localStorage.setItem('refresh_token', token);
    }
  };

  // Impersonation: start viewing as another user
  const startImpersonation = async (targetUserId) => {
    try {
      const response = await authApi.startImpersonation(targetUserId);
      const { token, refreshToken, impersonatedUser, realUser: realUserData } = response.data;

      // Store real user info in sessionStorage (survives page refresh, not tab close)
      setRealUser(realUserData);
      setIsImpersonating(true);
      sessionStorage.setItem('caro_impersonation', JSON.stringify({
        isImpersonating: true,
        realUser: realUserData,
      }));

      // Store impersonation tokens
      await setAccessToken(token);
      if (refreshToken) {
        await setRefreshToken(refreshToken);
      }

      // Update user state to impersonated user
      setUser(impersonatedUser);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to start impersonation';
      return { success: false, message: errorMessage };
    }
  };

  // Impersonation: stop and restore admin session
  const stopImpersonation = async () => {
    try {
      const response = await authApi.stopImpersonation();
      const { token, refreshToken, user: adminUser } = response.data;

      // Restore admin session
      await setAccessToken(token);
      if (refreshToken) {
        await setRefreshToken(refreshToken);
      }

      sessionStorage.removeItem('caro_impersonation');
      setIsImpersonating(false);
      setRealUser(null);
      setUser(adminUser);

      return { success: true };
    } catch (err) {
      // Even if API fails, clear impersonation state locally
      sessionStorage.removeItem('caro_impersonation');
      setIsImpersonating(false);
      setRealUser(null);

      const errorMessage = err.response?.data?.message || err.message || 'Failed to stop impersonation';
      return { success: false, message: errorMessage };
    }
  };

  // Listen for impersonation expired events (from apiClient interceptor)
  useEffect(() => {
    const handleImpersonationExpired = () => {
      sessionStorage.removeItem('caro_impersonation');
      setIsImpersonating(false);
      setRealUser(null);
      // User state will be updated by the token refresh response
    };

    window.addEventListener('impersonation-expired', handleImpersonationExpired);
    return () => window.removeEventListener('impersonation-expired', handleImpersonationExpired);
  }, []);

  // Value object to provide through the context
  const value = {
    user,
    loading,
    error,
    isPWA,
    deviceInfo,
    login,
    logout,
    checkAuth,
    handleAuthCallback,
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    isAuthenticated: !!user,
    // Impersonation state
    isImpersonating,
    realUser,
    startImpersonation,
    stopImpersonation,
    // Expose permission methods here for backward compatibility
    hasPermission: (permission) => {
      if (!user) return false;
      if (['admin', 'company_admin'].includes(user.role) && !isImpersonating) return true; // Full-access roles
      return user.permissions?.includes(permission) || false;
    },
    hasRole: (...roles) => {
      if (!user) return false;
      return roles.includes(user.role);
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};